import { Form, Radio, Switch, Tooltip } from 'antd';
import { useStoreState } from '~/store';
import { SortableList } from '../../shared/SortableList';
import type { SimulationType } from '~/store/simulation/types';
import { useParams } from 'react-router-dom';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  SELECTABLE_TNT_MODEL_SOURCES,
  TNT_MODEL_SOURCE_MAPPING,
  TNT_SETTING_MAPPING,
  TNT_SOURCE_PREFERENCE_MAPPING,
} from '~/store/simulation/types';
import { TntOptionsConfiguration } from '~/components/timeInTransit/TntOptionsConfiguration';

const { Item } = Form;

const TimeInTransitOptionsFormFields = {
  timeInTransitSourcePreference: 'timeInTransitSourcePreference',
  weekendConsideredTransitDay: 'weekendConsideredTransitDay',
  timeInTransitModelSource: 'timeInTransitModelSource',
  timeInTransitSetting: 'timeInTransitSetting',
} as const;

export const TimeInTransitOptionsForm = () => {
  const form = Form.useFormInstance();
  const { simulationType } = useParams<{ simulationType: SimulationType }>();
  const timeInTransitSourcePreference = Form.useWatch('timeInTransitSourcePreference');
  const timeInTransitModelSource = Form.useWatch('timeInTransitModelSource');
  const activePartner = useStoreState((state) => state.partner.activePartner);
  const isAdvancedSim = simulationType === 'advanced';

  return (
    <>
      <TntOptionsConfiguration activePartner={activePartner} isAdmin={isAdvancedSim} />
      <div style={{ display: 'flex', justifyContent: 'start', gap: '150px' }}>
        <div>
          <h3>
            Ranked simulation time in transit source preferences:
            <FormTooltip>
              <p>Used to prioritize the time in transit values used for delivery estimates</p>
              <ul style={{ listStylePosition: 'inside', paddingLeft: 0, margin: 0 }}>
                <li>Shipium Data uses ML modeling to create predictive estimates</li>
                <li>Partner overrides are custom transit time values that you can define and upload</li>
                <li>Carrier Defaults are calculated based on carrier-defined standards</li>
              </ul>
            </FormTooltip>
          </h3>
          <Item name={TimeInTransitOptionsFormFields.timeInTransitSourcePreference}>
            <SortableList
              value={timeInTransitSourcePreference}
              style={{ width: '450px', height: '42px' }}
              onChange={(d) => {
                form.setFieldValue('timeInTransitSourcePreference', d);
              }}
              optionsMap={TNT_SOURCE_PREFERENCE_MAPPING}
              exculdedItemsHeader="Add a source:"
              emptyItemsText="Defaulting to Production Configuration"
            />
          </Item>
        </div>
        <div>
          <Item
            valuePropName="checked"
            name={TimeInTransitOptionsFormFields.weekendConsideredTransitDay}
            label="Weekend Considered Transit Day"
          >
            <Switch />
          </Item>
          {isAdvancedSim && timeInTransitSourcePreference?.includes('shipium_calculated') && (
            <>
              <h3>Shipium Calculated TNT Configuration:</h3>
              <Item name={TimeInTransitOptionsFormFields.timeInTransitModelSource} label="TNT Model Source">
                <Radio.Group buttonStyle="outline">
                  {SELECTABLE_TNT_MODEL_SOURCES.map((source) => (
                    <Radio.Button key={source} value={source}>
                      {TNT_MODEL_SOURCE_MAPPING[source]}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </Item>
              {timeInTransitModelSource === 'shipment_delivery_prediction' && (
                <Item
                  name={TimeInTransitOptionsFormFields.timeInTransitSetting}
                  colon={false}
                  label={
                    <>
                      TNT Setting:
                      <FormTooltip>
                        <p>Used to configure the time in transit setting for the simulation</p>
                        <ul style={{ listStylePosition: 'inside', paddingLeft: 0, margin: 0 }}>
                          <li>
                            Conservative or Very Conservative - select when minimizing late delivery risk is the
                            priority
                          </li>
                          <li>Aggressive - select when optimizing shipping costs matters more than delivery timing</li>
                          <li>Standard - ideal for balancing delivery reliability with cost-effectiveness</li>
                        </ul>
                      </FormTooltip>
                    </>
                  }
                >
                  <Radio.Group buttonStyle="outline">
                    {Object.entries(TNT_SETTING_MAPPING).map(([key, value]) => (
                      <Radio.Button key={key} value={key}>
                        {value}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </Item>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

const FormTooltip = ({ children }: { children: React.ReactNode }) => {
  return (
    <Tooltip title={children}>
      <QuestionCircleOutlined style={{ marginLeft: 4, fontSize: 14 }} />
    </Tooltip>
  );
};
